import React from 'react';
import {
  Badge,
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  useTheme,
} from '@mui/material';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { alpha } from '@mui/material/styles';
import { borderRadius, layoutGap } from '../../../theme/theme';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import { createDisabledHelp } from '../../../shared/ui/common/helpModel';
import {
  exportActionTooltips,
  exportFieldTooltips,
} from '../../../shared/config/actionHelpCatalog';
import { OperationProgressIndicator } from '../../../shared/ui/progress';
import {
  getWorkflowWorkspacePalette,
  getWorkspaceSubpageHeaderSurfaceSx,
} from '../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import ExportScopeSummary from './ExportScopeSummary';
import {
  EXPORT_RAIL_CONTROL_SIZE,
  getExportRailActionButtonSx,
  getExportRailQuietIconButtonSx,
} from './exportRailControlStyles';

interface ExportToolbarProps {
  workspaceTitle?: string;
  workspaceActions?: React.ReactNode;
  toolbarRef?: React.Ref<HTMLDivElement>;
  systemCountRef?: React.Ref<HTMLElement>;
  systemsLoading?: boolean;
  systemsStale?: boolean;
  selectedCount: number;
  totalCount: number;
  systemNameFilterValue?: string;
  onSystemNameFilterChange?: (value: string) => void;
  exporting: boolean;
  batchNotValidatedCount: number;
  batchWarningCount: number;
  batchErrorCount: number;
  outputPath: string;
  onChangeOutputPath?: (path: string) => void;
  onBrowseOutputPath?: () => void;
  onSelectInModel?: () => void;
  onExportSelected: () => void;
  onExportAll: () => void;
  exportLogEntryCount?: number;
  onOpenExportLog?: () => void;
  modelSelecting?: boolean;
  selectionSummaryRef?: React.RefObject<HTMLDivElement | null>;
  outputPathRef?: React.RefObject<HTMLDivElement | null>;
  exportActionsRef?: React.RefObject<HTMLDivElement | null>;
  exportButtonRef?: React.RefObject<HTMLButtonElement | null>;
  pickAndExportButtonRef?: React.RefObject<HTMLButtonElement | null>;
  actionsDisabled?: boolean;
  actionsDisabledReason?: string;
  /** Overrides for the disabled-help card when the gate is not the
   *  mapping-profile one (e.g. the export element allowance is used up). */
  actionsDisabledTitle?: string;
  actionsDisabledNextStep?: string;
}

export default function ExportToolbar({
  workspaceTitle = 'Piping systems',
  workspaceActions,
  toolbarRef,
  systemCountRef,
  systemsLoading = false,
  systemsStale = false,
  selectedCount,
  totalCount,
  systemNameFilterValue = '',
  onSystemNameFilterChange,
  exporting,
  batchNotValidatedCount,
  batchWarningCount,
  batchErrorCount,
  outputPath,
  onChangeOutputPath,
  onBrowseOutputPath,
  onSelectInModel,
  onExportSelected,
  onExportAll,
  exportLogEntryCount = 0,
  onOpenExportLog,
  modelSelecting = false,
  selectionSummaryRef,
  outputPathRef,
  exportActionsRef,
  exportButtonRef,
  pickAndExportButtonRef,
  actionsDisabled = false,
  actionsDisabledReason,
  actionsDisabledTitle,
  actionsDisabledNextStep,
}: ExportToolbarProps) {
  const theme = useTheme();
  const workspacePalette = getWorkflowWorkspacePalette(theme);
  const secondaryActionSx = getExportRailActionButtonSx(theme, 'secondary');
  const primaryActionSx = getExportRailActionButtonSx(theme, 'primary');
  const quietIconButtonSx = getExportRailQuietIconButtonSx(theme);
  const hasSelection = selectedCount > 0;
  const trimmedOutputPath = outputPath.trim();
  const projectGateHelp = actionsDisabled
    ? createDisabledHelp(
        actionsDisabledTitle || 'Project mapping is not ready',
        actionsDisabledReason || 'Load and check a mapping profile for this Revit project before exporting PCF files.',
        {
          nextStep:
            actionsDisabledNextStep ||
            'Return to the mapping workspace and resolve the project readiness gate.',
        }
      )
    : null;
  const browseDisabled = actionsDisabled;
  // Pick-mode export ("Select in Model") hands control to Revit so the user
  // picks the elements to export directly in the model — it does NOT require any
  // pre-selected systems in the web table, so it stays enabled whenever the
  // project is ready and no export/pick is already in flight.
  const modelSelectionDisabled =
    !onSelectInModel || actionsDisabled || exporting || modelSelecting;
  const exportSelectedDisabled = actionsDisabled || selectedCount === 0 || exporting;
  const exportAllDisabled = actionsDisabled || totalCount === 0 || exporting;
  const railFieldSx = {
    width: '100%',
    '& .MuiInputBase-root': {
      height: EXPORT_RAIL_CONTROL_SIZE,
      fontSize: '0.75rem',
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
      borderRadius: `${borderRadius.sm}px`,
      '& fieldset': {
        borderColor: alpha(theme.palette.divider, 0.6),
      },
      '&:hover fieldset': {
        borderColor: alpha(theme.palette.text.secondary, 0.5),
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: '1px',
      },
    },
    '& .MuiInputBase-input': {
      py: 0,
      px: 1,
    },
  };
  return (
    <Box
      ref={toolbarRef}
      component="div"
      role="toolbar"
      aria-label="PCF export command bar"
      data-testid="export-pcf-tour-target-header"
      sx={{
        ...getWorkspaceSubpageHeaderSurfaceSx(workspacePalette, {
          sticky: false,
          density: 'compact',
        }),
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          sm: 'minmax(0, 1fr) auto',
          md: 'max-content minmax(180px, 1fr) max-content',
        },
        gridTemplateAreas: {
          xs: '"scope" "destination" "commands"',
          sm: '"scope commands" "destination destination"',
          md: '"scope destination commands"',
        },
        alignItems: 'center',
        rowGap: { xs: 0.25, sm: 0.5 },
        columnGap: layoutGap.dense,
        minWidth: 0,
        flexShrink: 0,
        overflowX: 'auto',
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: alpha(theme.palette.text.secondary, 0.24),
          borderRadius: `${borderRadius.full}px`,
        },
      }}
    >
      <ExportScopeSummary
        workspaceTitle={workspaceTitle}
        systemCountRef={systemCountRef}
        systemsLoading={systemsLoading}
        systemsStale={systemsStale}
        selectedCount={selectedCount}
        totalCount={totalCount}
        batchNotValidatedCount={batchNotValidatedCount}
        batchWarningCount={batchWarningCount}
        batchErrorCount={batchErrorCount}
        selectionSummaryRef={selectionSummaryRef}
      />

      <Box
        sx={{
          gridArea: 'destination',
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 0.85fr) minmax(0, 1.15fr)',
            sm: 'minmax(140px, 0.85fr) minmax(180px, 1.15fr)',
          },
          gap: layoutGap.dense,
          minWidth: 0,
          width: '100%',
        }}
      >
        <Box role="group" aria-label="Filter piping systems" sx={{ minWidth: 0 }}>
          <ActionHelpTooltip help={exportFieldTooltips.systemNameColumn} fullWidth wrapperSx={{ minWidth: 0 }}>
            <TextField
              size="small"
              type="search"
              value={systemNameFilterValue}
              onChange={(event) => onSystemNameFilterChange?.(event.target.value)}
              placeholder="Filter systems"
              inputProps={{
                'aria-label': 'Filter systems by name',
                'data-testid': 'export-system-filter-input',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: -0.25, mr: 0.25 }}>
                    <SearchIcon sx={{ fontSize: '0.95rem', color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: systemNameFilterValue ? (
                  <InputAdornment position="end" sx={{ mr: -0.5 }}>
                    <IconButton
                      size="small"
                      aria-label="Clear system filter"
                      onClick={() => onSystemNameFilterChange?.('')}
                      sx={{ width: 24, height: 24, p: 0, color: 'text.secondary' }}
                    >
                      <ClearIcon sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
              sx={railFieldSx}
            />
          </ActionHelpTooltip>
        </Box>
        <Box
          ref={outputPathRef}
          role="group"
          aria-label="Export destination"
          data-testid="export-pcf-tour-target-output-path"
          sx={{ minWidth: 0 }}
        >
          <ActionHelpTooltip help={exportFieldTooltips.destinationSummary} fullWidth wrapperSx={{ minWidth: 0 }}>
            <TextField
              size="small"
              value={outputPath}
              onChange={(e) => onChangeOutputPath?.(e.target.value)}
              placeholder="Output folder"
              disabled={browseDisabled}
              inputProps={{
                'aria-label': 'Export destination folder path',
                'data-testid': 'export-destination-input',
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: -0.5 }}>
                    <ActionHelpTooltip
                      help={exportActionTooltips.browseOutputPath}
                      disabledHelp={projectGateHelp || undefined}
                      disabled={browseDisabled}
                    >
                      <IconButton
                        size="small"
                        data-testid="export-browse-output-path"
                        aria-label="Change folder"
                        onClick={onBrowseOutputPath}
                        disabled={browseDisabled}
                        sx={quietIconButtonSx}
                      >
                        <FolderOpenIcon sx={{ fontSize: '0.95rem' }} />
                      </IconButton>
                    </ActionHelpTooltip>
                  </InputAdornment>
                ),
              }}
              sx={{
                ...railFieldSx,
                '& .MuiInputBase-input': {
                  py: 0,
                  px: 1,
                  color: trimmedOutputPath ? theme.palette.text.primary : theme.palette.text.secondary,
                },
              }}
            />
          </ActionHelpTooltip>
        </Box>
      </Box>

      <Box
        ref={exportActionsRef}
        role="group"
        data-testid="export-pcf-tour-target-actions"
        tabIndex={-1}
        aria-label="Export workspace commands"
        sx={{
          gridArea: 'commands',
          display: 'flex',
          alignItems: 'center',
          gap: layoutGap.dense,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          rowGap: 0.25,
          minWidth: 0,
          '& > *': {
            flexShrink: 0,
          },
        }}
      >
        {workspaceActions}
        <ActionHelpTooltip
          help={exportActionTooltips.exportLog}
          disabledHelp={exportActionTooltips.exportLogEmpty}
          disabled={!onOpenExportLog || exportLogEntryCount === 0}
        >
          <IconButton
            size="small"
            disabled={!onOpenExportLog || exportLogEntryCount === 0}
            onClick={onOpenExportLog}
            aria-label={exportLogEntryCount > 0
              ? `Open export log with ${exportLogEntryCount} entries`
              : 'Open export log'}
            sx={quietIconButtonSx}
          >
            <Badge
              badgeContent={exportLogEntryCount}
              color="primary"
              max={99}
              invisible={exportLogEntryCount === 0}
              sx={{ '& .MuiBadge-badge': { fontSize: '0.58rem', minWidth: 14, height: 14, px: 0.25 } }}
            >
              <ReceiptLongIcon sx={{ fontSize: '1rem' }} />
            </Badge>
          </IconButton>
        </ActionHelpTooltip>
        {onSelectInModel ? (
          <ActionHelpTooltip
            help={exportActionTooltips.selectSystemsInModel}
            // When the shared actionsDisabled gate (mapping readiness or
            // exhausted quota) is the cause, Pick must announce the same
            // composed reason as Export Selected/All — the generic
            // busy/pick help applies only to non-gate disabling.
            disabledHelp={projectGateHelp || exportActionTooltips.pickAndExportDisabled}
            disabled={modelSelectionDisabled}
          >
            <Button
              ref={pickAndExportButtonRef}
              variant="outlined"
              size="small"
              data-testid="export-select-in-model"
              startIcon={
                modelSelecting
                  ? <OperationProgressIndicator size={14} ariaLabel="Picking elements in the Revit model" />
                  : <TouchAppIcon />
              }
              disabled={modelSelectionDisabled}
              onClick={onSelectInModel}
              aria-label="Pick elements in the Revit model and export the picked elements to PCF"
              sx={[
                secondaryActionSx,
                {
                  height: EXPORT_RAIL_CONTROL_SIZE,
                  minHeight: EXPORT_RAIL_CONTROL_SIZE,
                  px: 1.05,
                },
              ]}
            >
              {modelSelecting ? 'Waiting for Revit…' : 'Pick in Revit'}
            </Button>
          </ActionHelpTooltip>
        ) : null}
        {hasSelection ? (
          <ActionHelpTooltip
            help={exportActionTooltips.exportSelected}
            disabledHelp={projectGateHelp || (exporting ? exportActionTooltips.exportInProgress : exportActionTooltips.exportSelectedDisabled)}
            disabled={exportSelectedDisabled}
          >
            <Button
              ref={exportButtonRef}
              variant="contained"
              size="small"
              data-testid="export-selected"
              startIcon={exporting ? <OperationProgressIndicator size={14} color="inherit" ariaLabel="Exporting selected systems" /> : <UploadFileIcon />}
              disabled={exportSelectedDisabled}
              onClick={onExportSelected}
              sx={[
                primaryActionSx,
                {
                  height: EXPORT_RAIL_CONTROL_SIZE,
                  minHeight: EXPORT_RAIL_CONTROL_SIZE,
                  px: 1.05,
                  transition: 'all 0.2s ease',
                },
              ]}
            >
              {`Export selected (${selectedCount})`}
            </Button>
          </ActionHelpTooltip>
        ) : (
          <ActionHelpTooltip
            help={exportActionTooltips.exportAll}
            disabledHelp={projectGateHelp || (exporting ? exportActionTooltips.exportInProgress : exportActionTooltips.exportAllDisabled)}
            disabled={exportAllDisabled}
          >
            <Button
              ref={exportButtonRef}
              variant="contained"
              size="small"
              data-testid="export-all"
              startIcon={exporting ? <OperationProgressIndicator size={14} color="inherit" ariaLabel="Exporting all systems" /> : <UploadFileIcon />}
              disabled={exportAllDisabled}
              onClick={onExportAll}
              sx={[
                primaryActionSx,
                {
                  height: EXPORT_RAIL_CONTROL_SIZE,
                  minHeight: EXPORT_RAIL_CONTROL_SIZE,
                  px: 1.05,
                  transition: 'all 0.2s ease',
                },
              ]}
            >
              Export all
            </Button>
          </ActionHelpTooltip>
        )}
      </Box>
    </Box>
  );
}
